'use client';

import { useCallback, useEffect, useState } from 'react';
import { DEFAULT_SEMESTER_ID } from '@/app/lib/semesters';
import { loadCatalogCourses } from '@/app/services/courseCatalogClient';
import type { RutgersCourse } from '@/app/types/interfaces';

export function useCatalogCoursesState(semester: string = DEFAULT_SEMESTER_ID): {
  courses: RutgersCourse[];
  isLoading: boolean;
  error: Error | null;
  retry: () => void;
} {
  const [requestVersion, setRequestVersion] = useState(0);
  const [state, setState] = useState<{
    semester: string; version: number; courses: RutgersCourse[]; isLoading: boolean; error: Error | null;
  }>({ semester, version: 0, courses: [], isLoading: true, error: null });

  useEffect(() => {
    let active = true;
    const request = { semester, version: requestVersion };
    setState({ ...request, courses: [], isLoading: true, error: null });
    void loadCatalogCourses(semester).then((courses) => {
      if (active) setState({ ...request, courses, isLoading: false, error: null });
    }).catch((reason: unknown) => {
      if (active) setState({ ...request, courses: [], isLoading: false, error: reason instanceof Error ? reason : new Error('Course catalog request failed.') });
    });
    return () => { active = false; };
  }, [requestVersion, semester]);

  const retry = useCallback(() => setRequestVersion(version => version + 1), []);
  // Fence during render as well as effect cleanup: even the first render after
  // switching terms cannot expose the prior term's rows or errors.
  const current = state.semester === semester && state.version === requestVersion;
  return { courses: current ? state.courses : [], isLoading: !current || state.isLoading, error: current ? state.error : null, retry };
}

export function useCatalogCourses(semester: string = DEFAULT_SEMESTER_ID): RutgersCourse[] {
  return useCatalogCoursesState(semester).courses;
}
